import { Component, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { GameEngineComponent } from '../game-engine/game-engine.component';
import { GamePluginRegistry } from '../services/game-plugin-registry.service';
import { WheelPlugin } from '../plugins/wheel';

/**
 * Example component demonstrating the game engine plugin system.
 * Shows how to:
 * 1. Register plugins
 * 2. Use the generic GameEngineComponent
 * 3. Update configuration dynamically
 * 4. Trigger game play
 */
@Component({
  selector: 'lib-game-engine-example',
  standalone: true,
  imports: [CommonModule, GameEngineComponent],
  template: `
    <div class="example-container">
      <h2>Game Engine Plugin System Example</h2>
      
      <div class="controls">
        <button (click)="changeRadius()">Change Radius</button>
        <button (click)="changeColors()">Change Colors</button>
        <button (click)="playSpin()">Play Spin</button>
      </div>

      <div class="game-display">
        <lib-game-engine
          #gameEngine
          [gameTypeId]="gameTypeId"
          [config]="gameConfig"
        ></lib-game-engine>
      </div>

      <div class="info" *ngIf="lastResult">
        <h3>Last Result:</h3>
        <p>Winner: {{ lastResult.isWinner ? 'Yes' : 'No' }}</p>
        <p *ngIf="lastResult.prize">Prize: {{ lastResult.prize.name }}</p>
      </div>
    </div>
  `,
  styles: [`
    .example-container {
      padding: 20px;
      max-width: 800px;
      margin: 0 auto;
    }

    .controls {
      margin: 20px 0;
      display: flex;
      gap: 10px;
    }

    button {
      padding: 10px 20px;
      background: #ed1f24;
      color: white;
      border: none;
      border-radius: 4px;
      cursor: pointer;
    }

    button:hover {
      background: #c71a1f;
    }

    .game-display {
      width: 500px;
      height: 500px;
      margin: 20px auto;
      border: 2px solid #ddd;
      border-radius: 8px;
    }

    .info {
      margin-top: 20px;
      padding: 15px;
      background: #f5f5f5;
      border-radius: 4px;
    }
  `]
})
export class GameEngineExampleComponent implements OnInit {
  private registry = inject(GamePluginRegistry);
  
  gameTypeId = 'wheel';
  gameConfig = {
    settings: {
      type: 'wheel' as const,
      radius: 0.84,
      borderColor: '#fff',
      borderWidth: 2,
      itemLabelRadius: 0.93,
      itemLabelRadiusMax: 0.35,
      itemLabelRotation: 180,
      itemLabelAlign: 'left' as const,
      itemLabelColors: ['#fff'],
      itemLabelFont: 'Codec Pro',
      itemLabelFontSizeMax: 55,
      itemLabelStrokeColor: '#000',
      itemLabelStrokeWidth: 0,
      isInteractive: false,
      rotationSpeedMax: 500,
      rotationResistance: 0,
      lineWidth: 1,
      lineColor: '#fff',
      itemBackgroundColors: ['#ed1f24', '#545454'],
    },
    items: [
      { label: 'Prize 1' },
      { label: '' },
      { label: 'Prize 2' },
      { label: '' },
      { label: 'Prize 3' },
      { label: '' },
      { label: 'Prize 4' },
      { label: '' },
    ],
  };

  lastResult: any = null;

  ngOnInit(): void {
    // Register the wheel plugin
    if (!this.registry.hasPlugin('wheel')) {
      this.registry.register(new WheelPlugin());
    }
  }

  changeRadius(): void {
    const newRadius = Math.random() * 0.3 + 0.6; // Random between 0.6 and 0.9
    this.gameConfig = {
      ...this.gameConfig,
      settings: {
        ...this.gameConfig.settings,
        radius: newRadius,
      },
    };
    console.log('Changed radius to:', newRadius);
  }

  changeColors(): void {
    const colors = [
      ['#ed1f24', '#545454'],
      ['#4CAF50', '#2196F3'],
      ['#FF9800', '#9C27B0'],
      ['#F44336', '#FFEB3B'],
    ];
    const randomColors = colors[Math.floor(Math.random() * colors.length)];
    this.gameConfig = {
      ...this.gameConfig,
      settings: {
        ...this.gameConfig.settings,
        itemBackgroundColors: randomColors,
      },
    };
    console.log('Changed colors to:', randomColors);
  }

  async playSpin(): Promise<void> {
    console.log('Playing spin...');
    // Note: In a real implementation, you would get a reference to the GameEngineComponent
    // and call its play() method. This is a simplified example.
    this.lastResult = {
      isWinner: Math.random() > 0.5,
      prize: { name: 'Example Prize', id: '1', type: 'physical' },
    };
  }
}
